// ============================================================================
// Pawn interest & insurance engine — FRONTEND COPY (ES module)
// ----------------------------------------------------------------------------
// MIRROR of the backend's utils/pawnInterestCalculator.js (titcketappbackend).
// Keep the two files in sync — the backend is authoritative; this copy powers
// instant/offline previews so the teller still sees correct figures when the
// preview API is unreachable. Zero dependencies, pure functions.
//
// Business rules implemented (July-2026 spec):
//
//   SETTINGS (store_pawn_settings / "company settings")
//   • master_ins / insuranceRate            – insurance % PER MONTH. 0 ⇒ none.
//   • no_interest_upto_2_days               – grace period (see precedence).
//   • pawn_monthly_interest                  – ON = monthly logic, OFF = daily.
//   • pawn_compound_interest                 – unpaid dues are added to the
//                                              principal AT RECEIPT TIME.
//   • less_than_15_days_as_half_month        – partial month ≤15 days ⇒ ½ month.
//   • less_than_15_days_as_full_month        – partial ⇒ full month (disables
//                                              the half-month benefit).
//   • one_month_deduct                       – 1st month prepaid at loan time.
//
//   PRECEDENCE around a due date D (extra = days paid after D):
//     1. extra ≤ 2  and grace ON  → charge the elapsed full months only.
//     2. extra ≤ 15 and half ON   → charge elapsed months + ½.
//     3. otherwise                → charge elapsed months + 1 (monthly mode)
//                                   / every actual day (daily mode).
//   The SAME precedence applies in the first cycle from the reference date
//   (within grace ⇒ 0; within 15 days & half ON ⇒ half month; else monthly
//   mode bills a 1-month minimum, daily mode bills actual days). Grace ON + the
//   whole loan cleared within 2 days of disbursement ⇒ 0 interest.
//
//   PAID-UP-TO INVARIANT: days(reference → paidUpToDate) = days charged.
//   e.g. loan 10-Apr: pay 12-May → 30 days, paid till 10-May;
//        pay 13..25-May → 45 days, paid till 25-May;
//        pay 26-May+    → 61 days, paid till 10-Jun.
//
//   DAILY MODE: a COMPLETE calendar month is charged the flat monthly amount
//   (principal × rate%), never day-summed (February = ₹100, not 28 × ₹4). Only
//   the leftover partial days beyond the last complete month are charged per
//   day at round(monthly interest ÷ days-in-month), rounded to the rupee BEFORE
//   multiplying. e.g. unpaid Feb, paid 3-Mar ⇒ ₹100 + 3 × ₹3 = ₹109.
//
//   INSURANCE mirrors every interest rule; only the rate differs.
//   SCHEME TIERS: by full calendar months since the LOAN date (only when the
//   scheme has "different interest for different month" on; else all NULL).
//   SCHEME BASE RATE: `interest` officially, `interest_2` in unofficial mode —
//   project the row through resolveSchemeForMode before calling computeDues.
//   COMPOUND: yearly. Shortfall carries over (paid before principal); ON ⇒
//   added to principal once a loan-year boundary is crossed.
// ============================================================================

// ── Date helpers (date-only semantics, no time-of-day, no TZ surprises) ─────
export const toDate = (d) => {
  if (d instanceof Date)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (typeof d === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  }
  const parsed = new Date(d);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

export const toISO = (d) => {
  const x = toDate(d);
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${mm}-${dd}`;
};

export const addDays = (d, n) => {
  const x = toDate(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const dateDiffDays = (from, to) => {
  const MS = 24 * 60 * 60 * 1000;
  return Math.round((toDate(to).getTime() - toDate(from).getTime()) / MS);
};

export const daysInMonth = (d) => {
  const x = toDate(d);
  return new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
};

export const addMonths = (d, n) => {
  const base = toDate(d);
  const day = base.getDate();
  const shifted = new Date(base.getFullYear(), base.getMonth() + n, 1);
  const last = new Date(
    shifted.getFullYear(),
    shifted.getMonth() + 1,
    0,
  ).getDate();
  shifted.setDate(Math.min(day, last));
  return shifted;
};

export const fullMonthsBetween = (from, to) => {
  const f = toDate(from);
  const t = toDate(to);
  let m =
    (t.getFullYear() - f.getFullYear()) * 12 + (t.getMonth() - f.getMonth());
  if (addMonths(f, m).getTime() > t.getTime()) m -= 1;
  return Math.max(0, m);
};

export const halfMonthDays = (d) => Math.floor(daysInMonth(d) / 2);

export const nearestRound = (value, step = 1) => {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Loan tenure in months — unpaid interest carries month to month; compounding
// (when ON) only capitalises the leftover into the principal once the loan
// reaches this many months. Overridable via options.tenureMonths.
const DEFAULT_LOAN_TENURE_MONTHS = 12;

// ── Daily-rate maths ─────────────────────────────────────────────────────────
export const dailyRateForMonth = (principal, monthlyRatePct, monthDate) =>
  Math.round((principal * monthlyRatePct) / 100 / daysInMonth(monthDate));

export const dailyInterestForSpan = (principal, monthlyRatePct, from, to) => {
  const start = toDate(from);
  const end = toDate(to);
  if (end.getTime() <= start.getTime()) return 0;
  let total = 0;
  let cursor = addDays(start, 1);
  while (cursor.getTime() <= end.getTime()) {
    const lastOfMonth = new Date(
      cursor.getFullYear(),
      cursor.getMonth(),
      daysInMonth(cursor),
    );
    const segEnd = lastOfMonth.getTime() < end.getTime() ? lastOfMonth : end;
    const segDays = dateDiffDays(cursor, segEnd) + 1;
    total += segDays * dailyRateForMonth(principal, monthlyRatePct, cursor);
    cursor = addDays(segEnd, 1);
  }
  return total;
};

// ── Scheme base rate by accounting mode ──────────────────────────────────────
// scheme_master carries TWO base rates: `interest` (official) and `interest_2`
// (unofficial). A loan booked in unofficial mode (pawn_broking.status = 2) is
// priced at interest_2; blank/0 falls back to `interest`, so a single-rate
// scheme behaves exactly as it always did.
export const schemeBaseRateForMode = (scheme, isUnofficial) => {
  if (!scheme) return 0;
  if (isUnofficial && num(scheme.interest_2) > 0) return num(scheme.interest_2);
  return num(scheme.interest);
};

// Copy of `scheme` whose `interest` is the base rate for this mode. Project the
// raw row through this ONCE, then hand the result to the engine — the backend
// does the same in loadLoanState, so the two can never quote different rates.
export const resolveSchemeForMode = (scheme, isUnofficial) => {
  if (!scheme) return null;
  return { ...scheme, interest: schemeBaseRateForMode(scheme, isUnofficial) };
};

// ── Scheme tier selection (months = full months since the LOAN date) ────────
// The `interest` read here is the mode-resolved base (see resolveSchemeForMode).
export const selectSchemeRate = (monthsSinceLoan, fallbackRatePct, scheme) => {
  if (!scheme) return fallbackRatePct;
  const base =
    num(scheme.interest) > 0 ? num(scheme.interest) : fallbackRatePct;
  let picked;
  if (monthsSinceLoan <= 1) picked = scheme.upto_month_1;
  else if (monthsSinceLoan <= 3) picked = scheme.upto_month_1_3;
  else if (monthsSinceLoan <= 12) picked = scheme.upto_month_3_12;
  else picked = scheme.after_12;
  return num(picked) > 0 ? num(picked) : base;
};

// ── Amount-wise slab selection (the OTHER interest basis) ──────────────────
// A store prices either month-wise (scheme_master, above) or amount-wise: the
// rate is decided by how big the loan is, once, when it is booked. Returns the
// rate of the band `amount` falls in, or null when no band covers it — null,
// not 0, so a caller can tell "the ladder says 0%" from "the ladder has nothing
// to say" and leave the field alone in the second case.
//
// BOTH bounds are INCLUSIVE, and a NULL/blank to_amount means no upper limit.
// Inclusive because the store types both ends itself — a band entered as
// "₹0 to ₹9,999" has to charge its rate ON ₹9,999.
//
// The ladder is deliberately NOT required to be contiguous: the bands are free
// text, so they may leave a hole or overlap. Overlaps resolve to the
// lowest-starting matching band (rows are sorted here, so the answer never
// depends on the order they were read back in). A hole returns null, and null
// is not 0 — a caller can tell "the ladder says 0%" from "no band covers this
// amount" and say so instead of quoting a rate nobody configured.
export const rateForLoanAmount = (amount, slabs) => {
  const amt = num(amount);
  if (!Array.isArray(slabs) || slabs.length === 0) return null;
  const ladder = slabs
    .filter((s) => s && s.interest !== null && s.interest !== undefined)
    .sort((a, b) => num(a.from_amount) - num(b.from_amount));
  for (const slab of ladder) {
    if (amt < num(slab.from_amount)) continue;
    const noCeiling =
      slab.to_amount === null ||
      slab.to_amount === undefined ||
      slab.to_amount === "";
    if (noCeiling || amt <= num(slab.to_amount)) return num(slab.interest);
  }
  return null;
};

// ── Normalisers (accept DB snake_case rows and camelCase objects) ───────────
export const normalizeSettings = (s = {}) => {
  const pick = (...keys) => {
    for (const k of keys) if (s[k] !== undefined && s[k] !== null) return s[k];
    return undefined;
  };
  const asBool = (v) => v === true || v === 1 || v === "1";
  return {
    monthly: asBool(pick("pawn_monthly_interest", "pawnMonthlyInterest")),
    compound: asBool(pick("pawn_compound_interest", "pawnCompoundInterest")),
    graceTwoDays: asBool(
      pick(
        "no_interest_upto_2_days",
        "noInterestFor2Days",
        "noInterestForTwoDays",
      ),
    ),
    halfMonth: asBool(
      pick("less_than_15_days_as_half_month", "lessThan15DaysAsHalfMonth"),
    ),
    fullMonthUnder15: asBool(
      pick("less_than_15_days_as_full_month", "lessThan15DaysAsFullMonth"),
    ),
    insuranceRate: num(pick("insurance_rate", "insuranceRate", "master_ins")),
    oneMonthDeduct: asBool(pick("one_month_deduct", "oneMonthDeduct")),
  };
};

export const normalizeLoan = (l = {}) => {
  const pick = (...keys) => {
    for (const k of keys) if (l[k] !== undefined && l[k] !== null) return l[k];
    return undefined;
  };
  return {
    principal: num(
      pick(
        "principal",
        "current_principal",
        "revised_principal",
        "loan_amount",
      ),
    ),
    loanDate: pick("loanDate", "loan_date", "transaction_date"),
    interestRate: num(pick("interestRate", "interest_rate", "loan_interest")),
    insuranceRate: num(
      pick("insuranceRate", "insurance_rate", "insurance_percentage"),
    ),
    carryoverInterest: num(pick("carryoverInterest", "carryover_interest")),
    carryoverInsurance: num(pick("carryoverInsurance", "carryover_insurance")),
    lastPaidUpToDate:
      pick("lastPaidUpToDate", "paid_up_to_date", "last_paid_up_to_date") ||
      null,
    oneMonthDeduct:
      pick("oneMonthDeduct", "one_month_deduct") === 1 ||
      pick("oneMonthDeduct", "one_month_deduct") === true,
  };
};

// ── Charge period (shared verbatim by interest and insurance) ───────────────
export const computeChargePeriod = (referenceDate, receiptDate, settings) => {
  const R = toDate(referenceDate);
  const P = toDate(receiptDate);
  const halfActive = settings.halfMonth && !settings.fullMonthUnder15;
  const graceDays = settings.graceTwoDays ? 2 : 0;

  if (P.getTime() <= R.getTime()) {
    return {
      branch: "already-covered",
      fullMonths: 0,
      monthsCharged: 0,
      dueDate: addMonths(R, 1),
      extraDays: 0,
      halfExtraDays: 0,
      chargeEndDate: R,
      paidUpToDate: R,
    };
  }

  const M = fullMonthsBetween(R, P);

  if (M === 0) {
    if (settings.monthly) {
      const elapsed = dateDiffDays(R, P);
      // Grace buffer measured from the reference (loan date / last paid-up-to).
      if (graceDays > 0 && elapsed <= graceDays) {
        return {
          branch: "grace-2-days",
          fullMonths: 0,
          monthsCharged: 0,
          dueDate: addMonths(R, 1),
          extraDays: elapsed,
          halfExtraDays: 0,
          chargeEndDate: R,
          paidUpToDate: P,
        };
      }
      // ≤15 days into the cycle with the half-month benefit ON ⇒ HALF a month,
      // settling the customer through the full cycle (reference + 1 month).
      if (halfActive && elapsed <= 15) {
        return {
          branch: "half-month",
          fullMonths: 0,
          monthsCharged: 0.5,
          dueDate: addMonths(R, 1),
          extraDays: elapsed,
          halfExtraDays: 0,
          chargeEndDate: addMonths(R, 1),
          paidUpToDate: addMonths(R, 1),
        };
      }
      return {
        branch: "minimum-one-month",
        fullMonths: 0,
        monthsCharged: 1,
        dueDate: addMonths(R, 1),
        extraDays: 0,
        halfExtraDays: 0,
        chargeEndDate: addMonths(R, 1),
        paidUpToDate: addMonths(R, 1),
      };
    }
    // ── Daily, first cycle (no full month yet) ────────────────────────────────
    const elapsedD = dateDiffDays(R, P);
    // Grace (global, both modes): within the 2-day buffer ⇒ nothing charged.
    if (graceDays > 0 && elapsedD <= graceDays) {
      return {
        branch: "grace-2-days",
        fullMonths: 0,
        monthsCharged: 0,
        dueDate: addMonths(R, 1),
        extraDays: elapsedD,
        halfExtraDays: 0,
        chargeEndDate: R,
        paidUpToDate: P,
      };
    }
    // Half-month: paid within the first 15 days ⇒ charge HALF of this calendar
    // month's days at its per-day rate (Feb 14×₹4=₹56, others 15×₹3=₹45).
    if (halfActive && elapsedD <= 15) {
      const halfD = halfMonthDays(R);
      return {
        branch: "half-month",
        fullMonths: 0,
        monthsCharged: 0.5,
        dueDate: addMonths(R, 1),
        halfRateDate: R,
        extraDays: elapsedD,
        halfExtraDays: halfD,
        chargeEndDate: R,
        paidUpToDate: addDays(R, halfD),
      };
    }
    return {
      branch: "daily-actual-days",
      fullMonths: 0,
      monthsCharged: 0,
      dueDate: addMonths(R, 1),
      extraDays: 0,
      halfExtraDays: 0,
      chargeEndDate: P,
      paidUpToDate: P,
    };
  }

  const D = addMonths(R, M);
  const extra = dateDiffDays(D, P);

  if (extra === 0 || extra <= graceDays) {
    return {
      branch: extra === 0 ? "exact-months" : "grace-2-days",
      fullMonths: M,
      monthsCharged: M,
      dueDate: D,
      extraDays: extra,
      halfExtraDays: 0,
      chargeEndDate: D,
      paidUpToDate: D,
    };
  }

  if (halfActive && extra <= 15) {
    const half = halfMonthDays(D);
    return {
      branch: "half-month",
      fullMonths: M,
      monthsCharged: M + 0.5,
      dueDate: D,
      extraDays: extra,
      halfExtraDays: half,
      chargeEndDate: D,
      paidUpToDate: addDays(D, half),
    };
  }

  return {
    branch: "full-extra-month",
    fullMonths: M,
    monthsCharged: M + 1,
    dueDate: D,
    extraDays: extra,
    halfExtraDays: 0,
    chargeEndDate: settings.monthly ? addMonths(D, 1) : P,
    paidUpToDate: settings.monthly ? addMonths(D, 1) : P,
  };
};

const chargeAmount = (
  principal,
  ratePct,
  referenceDate,
  period,
  settings,
  roundStep,
) => {
  if (principal <= 0 || ratePct <= 0) return 0;
  if (settings.monthly) {
    return nearestRound(
      (principal * ratePct * period.monthsCharged) / 100,
      roundStep,
    );
  }
  // ── Daily mode ─────────────────────────────────────────────────────────────
  // A COMPLETE calendar month is always charged the flat monthly amount
  // (principal × rate%), never day-summed — so a passed month costs exactly one
  // month's interest with no per-day rounding drift (February = ₹100, not
  // 28 × ₹4 = ₹112). Only the leftover partial days BEYOND the last complete
  // month are charged per day at that calendar month's rounded daily rate.
  //   e.g. unpaid Feb, paid 3-Mar ⇒ ₹100 (Feb) + 3 × ₹3 (Mar) = ₹109.
  const monthlyInterest = (principal * ratePct) / 100;
  const completeMonthsEnd = addMonths(referenceDate, period.fullMonths);
  let amount = period.fullMonths * monthlyInterest;
  amount += dailyInterestForSpan(
    principal,
    ratePct,
    completeMonthsEnd,
    period.chargeEndDate,
  );
  if (period.halfExtraDays > 0) {
    amount +=
      period.halfExtraDays *
      dailyRateForMonth(principal, ratePct, period.halfRateDate || period.dueDate);
  }
  return nearestRound(amount, roundStep);
};

const chargedDays = (referenceDate, period, settings) => {
  if (settings.monthly) {
    return Math.max(0, dateDiffDays(referenceDate, period.paidUpToDate));
  }
  return (
    Math.max(0, dateDiffDays(referenceDate, period.chargeEndDate)) +
    period.halfExtraDays
  );
};

// ============================================================================
// computeDues — interest + insurance due as of a date (NO allocation).
// ============================================================================
export const computeDues = (
  loan,
  asOfDate,
  companySettings,
  scheme = null,
  options = {},
) => {
  const L = normalizeLoan(loan);
  const S = normalizeSettings(companySettings);
  const roundStep = options.roundStep === undefined ? 1 : options.roundStep;
  const P = toDate(asOfDate);

  // one_month_deduct is decided PER LOAN at disbursement and stored on the loan
  // row — that value is authoritative here. The store-level setting is only the
  // default applied when a loan is created, so it must NOT override a loan that
  // explicitly opted out (otherwise such a loan wrongly shows its first month as
  // prepaid and asks for zero interest on the first receipt).
  const oneMonthDeduct = L.oneMonthDeduct;
  const firstDueDate = addMonths(L.loanDate, 1);
  const hasReceipts = !!L.lastPaidUpToDate;

  const monthsSinceLoan = fullMonthsBetween(L.loanDate, P);
  const interestRate = scheme
    ? selectSchemeRate(monthsSinceLoan, L.interestRate, scheme)
    : L.interestRate;
  // Insurance rate comes from the LOAN row only — no fallback to the store
  // master rate. It is stored at disbursement ONLY when one_month_deduct was on;
  // a loan that opted out stores 0 and must not be charged insurance.
  const insuranceRate = L.insuranceRate;

  // Audit trail of the inputs that decided the charge — carried on every result
  // so callers can EXPLAIN the figure (the receipt screen's price breakdown)
  // without re-deriving any of it. Presentation-free: numbers and flags only.
  const audit = {
    loanDate: toISO(L.loanDate),
    receiptDate: toISO(P),
    oneMonthDeduct,
    rules: {
      monthly: S.monthly,
      compound: S.compound,
      graceDays: S.graceTwoDays ? 2 : 0,
      halfMonth: S.halfMonth && !S.fullMonthUnder15,
      fullMonthUnder15: S.fullMonthUnder15,
    },
  };

  const base = {
    principal: L.principal,
    appliedInterestRate: interestRate,
    appliedInsuranceRate: insuranceRate,
    carryoverInterestBefore: L.carryoverInterest,
    carryoverInsuranceBefore: L.carryoverInsurance,
    monthsSinceLoan,
    ...audit,
  };

  const zeroCharge = (branch, paidUpTo, referenceDate) => ({
    ...base,
    branch,
    referenceDate: toISO(referenceDate),
    dueDate: toISO(addMonths(referenceDate, 1)),
    monthsCharged: 0,
    daysCharged: 0,
    fullMonths: 0,
    extraDays: 0,
    halfExtraDays: 0,
    periodInterest: 0,
    periodInsurance: 0,
    interestDue: nearestRound(L.carryoverInterest, roundStep),
    insuranceDue: nearestRound(L.carryoverInsurance, roundStep),
    paidUpToDate: toISO(paidUpTo),
  });

  if (!hasReceipts && oneMonthDeduct && P.getTime() <= firstDueDate.getTime()) {
    return zeroCharge("prepaid-first-month", firstDueDate, L.loanDate);
  }

  if (
    !hasReceipts &&
    !oneMonthDeduct &&
    S.graceTwoDays &&
    dateDiffDays(L.loanDate, P) <= 2
  ) {
    return zeroCharge("loan-2-day-grace", P, L.loanDate);
  }

  const referenceDate = hasReceipts
    ? toDate(L.lastPaidUpToDate)
    : oneMonthDeduct
      ? firstDueDate
      : toDate(L.loanDate);

  const period = computeChargePeriod(referenceDate, P, S);

  const periodInterest = chargeAmount(
    L.principal,
    interestRate,
    referenceDate,
    period,
    S,
    roundStep,
  );
  const periodInsurance =
    insuranceRate > 0
      ? chargeAmount(
          L.principal,
          insuranceRate,
          referenceDate,
          period,
          S,
          roundStep,
        )
      : 0;

  return {
    ...base,
    branch: period.branch,
    referenceDate: toISO(referenceDate),
    dueDate: toISO(period.dueDate),
    monthsCharged: period.monthsCharged,
    daysCharged: chargedDays(referenceDate, period, S),
    // Audit: complete months elapsed since the reference, days paid AFTER the
    // governing due date (what the grace / half-month / full-month precedence
    // was decided on), and the ½-month days the half-month branch added.
    fullMonths: period.fullMonths,
    extraDays: period.extraDays,
    halfExtraDays: period.halfExtraDays,
    periodInterest,
    periodInsurance,
    interestDue: nearestRound(periodInterest + L.carryoverInterest, roundStep),
    insuranceDue: nearestRound(
      periodInsurance + L.carryoverInsurance,
      roundStep,
    ),
    paidUpToDate: toISO(period.paidUpToDate),
  };
};

// ============================================================================
// allocatePayment — split money across interest → insurance → principal.
// ----------------------------------------------------------------------------
// The allocation half of processReceipt, factored out so a UI can show exactly
// how a given amount WILL be split (the receipt screen's price breakdown) using
// the same code that books it — the two can never disagree.
//
//   dues   – { interestDue, insuranceDue, principal } (a computeDues result).
//   amount – money available for the loan (other charges already removed).
//   options.interestDiscount / interestPaidOverride / insurancePaidOverride.
// ============================================================================
export const allocatePayment = (dues, amount, options = {}) => {
  const money = Math.max(0, num(amount));
  const discount = Math.max(0, num(options.interestDiscount));

  // Net dues after any teller discount (discount eats interest only).
  const interestDueNet = Math.max(0, num(dues.interestDue) - discount);
  const insuranceDueNet = num(dues.insuranceDue);

  const interestDeduction =
    options.interestPaidOverride != null
      ? Math.min(num(options.interestPaidOverride), money)
      : Math.min(interestDueNet, money);
  const insuranceDeduction =
    options.insurancePaidOverride != null
      ? Math.min(num(options.insurancePaidOverride), money - interestDeduction)
      : Math.min(insuranceDueNet, money - interestDeduction);

  const shortfallInterest = Math.max(0, interestDueNet - interestDeduction);
  const shortfallInsurance = Math.max(0, insuranceDueNet - insuranceDeduction);

  const afterDues = money - interestDeduction - insuranceDeduction;
  const principalReduction = Math.min(
    num(dues.principal),
    Math.max(0, afterDues),
  );
  const excessAmount = Math.max(0, afterDues - principalReduction);

  return {
    receiptAmount: money,
    interestDiscount: discount,
    interestDueNet,
    insuranceDueNet,
    interestDeduction,
    insuranceDeduction,
    shortfallInterest,
    shortfallInsurance,
    principalReduction,
    excessAmount,
  };
};

// ============================================================================
// processReceipt — allocation of a payment (interest → insurance → principal).
// ============================================================================
export const processReceipt = (
  loan,
  receiptAmount,
  receiptDate,
  companySettings,
  scheme = null,
  options = {},
) => {
  const S = normalizeSettings(companySettings);
  const dues = computeDues(loan, receiptDate, companySettings, scheme, options);
  const roundStep = options.roundStep === undefined ? 1 : options.roundStep;

  // ── Allocate: interest → insurance → principal ─────────────────────────────
  const {
    receiptAmount: amount,
    interestDiscount: discount,
    interestDeduction,
    insuranceDeduction,
    shortfallInterest,
    shortfallInsurance,
    principalReduction,
    excessAmount,
  } = allocatePayment(dues, receiptAmount, options);

  // Compounding is YEARLY: leftover only capitalises into the principal when a
  // loan-year boundary (tenure, 2×tenure, …) is crossed since the last receipt.
  // Within a year the shortfall just carries. Compare completed years at the
  // reference (last paid-through) vs now; a jump means a boundary was crossed.
  const tenureMonths =
    options.tenureMonths != null
      ? num(options.tenureMonths)
      : DEFAULT_LOAN_TENURE_MONTHS;
  const loanForYears = normalizeLoan(loan);
  const yearsBefore = Math.floor(
    fullMonthsBetween(loanForYears.loanDate, dues.referenceDate) / tenureMonths,
  );
  const yearsNow = Math.floor(dues.monthsSinceLoan / tenureMonths);
  const crossedYearBoundary = yearsNow > yearsBefore;
  const capitalize = S.compound && crossedYearBoundary;
  const compoundedAmount = capitalize
    ? nearestRound(shortfallInterest + shortfallInsurance, roundStep)
    : 0;
  const newPrincipal = Math.max(
    0,
    dues.principal - principalReduction + compoundedAmount,
  );
  const carryoverInterest = capitalize ? 0 : shortfallInterest;
  const carryoverInsurance = capitalize ? 0 : shortfallInsurance;

  return {
    ...dues,
    receiptDate: toISO(receiptDate),
    receiptAmount: amount,
    interestDiscount: discount,
    interestDeduction,
    insuranceDeduction,
    principalReduction,
    excessAmount,
    shortfallInterest,
    shortfallInsurance,
    compounded: compoundedAmount > 0,
    compoundedAmount,
    newPrincipal,
    carryoverInterest,
    carryoverInsurance,
  };
};
